import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  askAssistant,
  assistantErrorMessage,
  getAssistantStatus,
  type ChatTurn,
} from "../api/assistant";

const SUGGESTIONS = [
  "Why isn't my campaign dialing?",
  "How do I upload a list with custom fields?",
  "What do the disposition buttons mean?",
  "How does the do-not-call list work?",
];

export function AssistantPage() {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const { data: enabled, isLoading: checking } = useQuery({
    queryKey: ["assistant-status"],
    queryFn: getAssistantStatus,
    staleTime: 60_000,
  });

  const ask = useMutation({
    mutationFn: (question: string) => askAssistant(question, turns),
    onSuccess: (answer) =>
      setTurns((t) => [...t, { role: "assistant", content: answer }]),
    onError: (err) => {
      setError(assistantErrorMessage(err));
      // Drop the unanswered question so the thread doesn't look answered.
      setTurns((t) => (t.length && t[t.length - 1].role === "user" ? t.slice(0, -1) : t));
    },
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, ask.isPending]);

  function send(question: string) {
    const q = question.trim();
    if (!q || ask.isPending) return;
    setError(null);
    setTurns((t) => [...t, { role: "user", content: q }]);
    setDraft("");
    ask.mutate(q);
  }

  if (!checking && !enabled) {
    return (
      <div className="max-w-3xl">
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Assistant</h1>
        <p className="text-sm text-gray-600">
          The assistant isn't switched on for this account yet. In the meantime,
          the <a className="text-brand underline" href="/changelog">Changelog</a> covers
          recent changes, and Support can help with anything urgent.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl flex flex-col" style={{ height: "calc(100vh - 9rem)" }}>
      <div className="mb-3">
        <h1 className="text-xl font-semibold text-gray-900">Assistant</h1>
        <p className="text-sm text-gray-500">
          Ask how something works, or why a campaign is behaving the way it is.
          It can see your own account to help answer — and it can only give
          advice, never change anything.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto rounded-2xl bg-white shadow p-4 space-y-4">
        {turns.length === 0 && (
          <div className="text-sm text-gray-500">
            <p className="mb-3">Try one of these:</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="rounded-full border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:border-brand hover:text-brand transition"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {turns.map((t, i) => (
          <div key={i} className={t.role === "user" ? "text-right" : ""}>
            <div
              className={`inline-block max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap text-left ${
                t.role === "user"
                  ? "bg-brand text-white"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {t.content}
            </div>
          </div>
        ))}

        {ask.isPending && (
          <div className="text-sm text-gray-400" role="status">Thinking…</div>
        )}
        {error && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
            {error}
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form
        className="mt-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          send(draft);
        }}
      >
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask a question…"
          aria-label="Ask a question"
          className="flex-1 rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
        />
        <button
          type="submit"
          disabled={ask.isPending || !draft.trim()}
          className="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white disabled:opacity-50"
        >
          Send
        </button>
      </form>
      <p className="mt-2 text-xs text-gray-400">
        Answers can be wrong. For anything involving money, a legal notice, or an
        outage, contact Support.
      </p>
    </div>
  );
}
